import * as React from "react";
import { useState, useEffect } from "react";
import DialogContentText from "@mui/material/DialogContentText";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

import arrow from "../../../../../../styles/svg/arrow.svg";
import { updateBoard } from "../../../../../../sevices/apiCards";
import {
  useWorkflowStepNameByWorkflowStepId,
  useWorkflowById,
} from "../../../../../../utils/reactQuery/workflows";
import "./ChangeBoardToDifferentWorkflow.scss";
import { useResponsive } from "../../../../../../hooks/useResponsive";
import useToastPromise from "../../../../../../hooks/useToast";
import CustomMenuItem from "../../../../../../components/Menu/CustomMenuItem";
import FormDialog from "../../../../../../components/Dialog/FormDialog";
import { RFHTextField, RHFSelect } from "../../../../../../components/RFH";
import useMutate from "../../../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../../../hooks/useBoardIdByKey";

export default function ChangeBoardToDifferentWorkflow({
  open,
  close,
  card,
  currentBoardName,
  targetBoardName,
  workflowId,
  cardId,
  targetBoardId,
  currentBoardWorkflowId,
  currentWorkflowStepId,
  isTodo,
}) {
  const [currentWorkflowStepName, setCurrentWorkflowStepName] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState(null);
  const { id } = useBoardIdByKey();
  const queryClient = useQueryClient();
  const isMobile = useResponsive("down", "md");
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToastPromise } = useToastPromise();
  const { data, refetch } = useWorkflowById(workflowId, workflowId);
  const { workflowStepName } = useWorkflowStepNameByWorkflowStepId(
    card?.data?.data?.responseData?.workflow_step_id,
    card?.data?.data?.responseData?.board_id
  );
  const {
    register,
    handleSubmit: updateBoardSubmit,
    watch,
    ...methods
  } = useForm({
    targetStep: data?.data?.data?.responseData[0]?.workflow_step_id || "",
  });

  const targetWorkflowStep = watch("targetStep");
  useEffect(() => {
    if (data) {
      setWorkflowSteps(data);
    }
  }, [workflowId, data]);

  useEffect(() => {
    if (card?.data?.data?.responseData?.workflow_step_id) {
      setCurrentWorkflowStepName(workflowStepName);
    }
  }, [card, workflowStepName]);

  const { mutate: updateMutate, isPending } = useMutate(updateBoard, [`boards`, `watching`, `mineCards`],
    () => { },
    () => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("brd_id", targetBoardId);
      setSearchParams(newSearchParams);
      queryClient.invalidateQueries({
        queryKey: [`cards`, id],
      });
      queryClient.invalidateQueries({
        queryKey: [`card`, cardId],
      });
      queryClient.invalidateQueries({
        queryKey: [`history`, String(cardId)],
      });
      close(false);
    },
    () => { },
  );

  const onSubmit = (data) => {
    updateMutate({
      card_id: cardId,
      board_id: targetBoardId,
      ...(!isTodo ? { workflow_step_id: data?.targetStep ?? currentWorkflowStepId } : {}),
    });
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={updateBoardSubmit(onSubmit)}>
          <FormDialog
            open={open}
            handleClose={() => close(false)}
            title={"Move Task to Another Board"}
            buttonTxt={"Move"}
            width={494}
            fullWidth={true}
            disabled={
              isTodo
                ? false
                : (currentBoardWorkflowId !== workflowId || isPending) &&
                (!targetWorkflowStep || isPending)
            }
            handleSubmit={updateBoardSubmit(onSubmit)}
          >
            <div className="move-ticket-dialog-content">
              {currentBoardWorkflowId !== workflowId && !isTodo ? (
                <DialogContentText className="move-ticket-dialog-contentText">
                  Origin and Destination boards use different workflow steps,
                  select the step you want to put this task in when moving it to
                  the destination board.
                </DialogContentText>
              ) : (
                <DialogContentText className="move-ticket-dialog-contentText">
                  Origin and Destination boards use the same workflow steps, so
                  the task will automatically move to its corresponding step in
                  the destination board.
                </DialogContentText>
              )}
              {currentBoardWorkflowId !== workflowId && !isTodo && (
                <div className="move-ticket-form-dialogContent">
                  <div
                    className={
                      isMobile
                        ? "mobile-ticket-reasign-step-container"
                        : "ticket-reasign-step-container"
                    }
                  >
                    <div
                      className={
                        isMobile
                          ? "mobile-current-target-board-name"
                          : "current-target-board-name"
                      }
                    >
                      <p> {currentBoardName} </p>
                      <div className="initial-step">
                        <RFHTextField
                          name={"test"}
                          className="mui-status-field change-board-workflow"
                          disabled
                          value={
                            currentWorkflowStepName?.data?.data?.responseData
                              ?.status
                          }
                          defaultPadding={false}
                        />
                      </div>
                    </div>
                    <img src={arrow} alt="" />
                    <div
                      className={
                        isMobile
                          ? "mobile-current-target-board-name"
                          : "current-target-board-name"
                      }
                    >
                      <p>{targetBoardName}</p>
                      <div className="initial-step">
                        <RHFSelect
                          name="targetStep"
                          sx={{
                            width: isMobile ? "100%" : "180px",
                          }}
                          className="mui-status-field"
                          required
                        >
                          {workflowSteps?.data?.data?.responseData
                            ?.sort((a, b) => a.step_sequence - b.step_sequence)
                            .map((option) => (
                              <CustomMenuItem
                                key={option?.workflow_step_id}
                                id={option?.workflow_step_id}
                                value={option.workflow_step_id}
                                className="uppercase"
                              >
                                {option.status}
                              </CustomMenuItem>
                            ))}
                        </RHFSelect>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </FormDialog>
        </form>
      </FormProvider>
    </React.Fragment>
  );
}
